// User data management - search history, scouts, metrics
import api from './axiosConfig';

// Normalize API search history item to UI shape
function normalizeSearchHistoryItem(r) {
  return {
    id: r.id,
    query: r.query,
    filters: r.filters || {},
    resultCount: r.resultCount ?? r.result_count ?? 0,
    topResults: r.topResults || r.top_results || [],
    timestamp: r.timestamp || r.created_at
  };
}

export const userData = {
  // Save a search query to the database (replaces sessionStorage)
  saveSearch: async (userId, query, results, filters) => {
    const topResults = (results || []).slice(0, 3).map(p => ({
      title: p.title,
      price: p.price,
      city: p.city,
      url: p.url ?? null
    }));
    const resultCount = Array.isArray(results) ? results.length : 0;
    try {
      const response = await api.post('/api/search-history', {
        query,
        filters: filters || {},
        resultCount,
        topResults
      });
      const data = response.data;
      return data ? normalizeSearchHistoryItem(data) : null;
    } catch (error) {
      console.error('Error saving search history:', error);
      throw error;
    }
  },

  // Fetch one page of search history (for Profile tab). Returns { items, total }.
  fetchSearchHistoryPage: async (limit = 3, offset = 0) => {
    try {
      const response = await api.get('/api/search-history', { params: { limit, offset } });
      const items = (response.data?.items || []).map(normalizeSearchHistoryItem);
      const total = response.data?.total ?? items.length;
      return { items, total };
    } catch (error) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        return { items: [], total: 0 };
      }
      console.error('Error fetching search history:', error);
      return { items: [], total: 0 };
    }
  },

  // Get user metrics (search stats from API + scouts from API)
  getMetrics: async (userId) => {
    let searchStats = {
      totalSearches: 0,
      totalResults: 0,
      avgResults: 0,
      topCities: [],
      avgMaxPrice: 0,
      lastSearch: null
    };
    try {
      const res = await api.get('/api/search-history/stats');
      searchStats = {
        totalSearches: res.data?.totalSearches ?? 0,
        totalResults: res.data?.totalResults ?? 0,
        avgResults: res.data?.avgResults ?? 0,
        topCities: res.data?.topCities ?? [],
        avgMaxPrice: res.data?.avgMaxPrice ?? 0,
        lastSearch: res.data?.lastSearch ?? null
      };
    } catch (error) {
      console.error('Error fetching search history stats for metrics:', error);
    }
    let userScouts = [];
    try {
      const { scouts: scoutsUtil } = await import('./scouts');
      userScouts = await scoutsUtil.fetchUserScouts();
    } catch (error) {
      console.error('Error fetching scouts for metrics:', error);
    }
    return {
      ...searchStats,
      activeScouts: userScouts.length
    };
  },

  // Get user profile from API (no longer uses localStorage)
  getProfile: async (userId) => {
    if (!userId) return null;
    try {
      const response = await api.get(`/api/users/${userId}`);
      const profile = response.data?.user || null;
      return profile;
    } catch (error) {
      // 404 means user doesn't exist yet, which is fine
      if (error.response?.status === 404) {
        return null;
      }
      console.error('Error fetching user profile:', error);
      return null;
    }
  }
};

